import {useMemo} from "react"
import {groupBy, uniqBy} from "lodash"
import gql from "graphql-tag"
import {Budget, BudgetCategories, BudgetUnassigned, Business, User, QueryBusinessArgs} from "@irati/types"
import {UseQueryExtend, createUseQuery} from "@irati/utils/services/apollo"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {FullWalletFragment} from "../../graph/fragments/fullWallet"
import {BudgetAny} from "./shared"

interface CompanyBudgets {
  total: number
  budgets: BudgetCategories
}

const useCompanyBudgetsQueryBase = createUseQuery<Business, QueryBusinessArgs>(
  gql`
    query CompanyBudgets($businessId: ID!) {
      business(businessId: $businessId) {
        uuid
        globalAdmins {
          uuid
          firstName
          lastName
          rewardWallet {
            ...FullWalletFragment
          }
        }
        teamManagers {
          uuid
          firstName
          lastName
          rewardWallet {
            ...FullWalletFragment
          }
        }
      }
    }

    ${FullWalletFragment}
  `,
  {
    key: "business"
  }
)

type UseCompanyBudgetsTotalQuery = UseQueryExtend<typeof useCompanyBudgetsQueryBase, {data: CompanyBudgets}>

export const useCompanyBudgetsQuery = (): UseCompanyBudgetsTotalQuery => {
  const {user} = useAuth({})
  const {data, ...apollo} = useCompanyBudgetsQueryBase({businessId: user.businessId!})

  const budgets = useMemo(() => {
    if (!data) return undefined
    const uniqueAdmins = uniqBy(data.globalAdmins.concat(data.teamManagers), (u) => u.uuid)
    return aggregateAllBudgets(uniqueAdmins)
  }, [data])

  return {
    ...apollo,
    data: budgets
  }
}

function aggregateAllBudgets(data: User[]) {
  const companyTotal = data
    .map((user) => user.rewardWallet?.amount)
    .filter((n): n is number => !!n)
    .reduce((total, budget) => total + budget, 0)

  const unassigned = data
    .map((user) => user.rewardWallet?.budgets?.unassigned)
    .filter((b): b is BudgetUnassigned => !!b)
    .reduce(sumAmounts(companyTotal))

  const groupedCategories = groupBy(
    data.flatMap((user) => user.rewardWallet?.budgets?.categories).filter((b): b is Budget => !!b),
    (b) => b.uuid
  )

  const budgets = {
    unassigned,
    categories: Object.values(groupedCategories)
      .map((budgetList) => budgetList.reduce(sumAmounts(companyTotal)))
      .sort((a, b) => b.amount - a.amount)
  }

  return {
    budgets,
    total: companyTotal
  }
}

function sumAmounts<A extends BudgetAny>(companyTotal: number) {
  return (finalBudget: A | undefined, budget: A): A => {
    if (!finalBudget) return budget
    const amount = finalBudget?.amount + budget?.amount ?? 0
    return {...finalBudget, amount, percentage: (amount / companyTotal) * 100}
  }
}
